/**
 * HeroCarousel — Premium Multi-Item Hero mit Crossfade.
 * -----------------------------------------------------
 * - 1 Item  → statisch, ohne Dots.
 * - ≥2 Items → Crossfade (opacity 800ms) + Auto-Advance 6s + Dots + Swipe.
 * - Pause bei Hover / Focus-within (a11y).
 * - Respektiert `prefers-reduced-motion: reduce` (kein Auto-Advance, kein Fade).
 * - WebGL-Safety: Nur der AKTIVE Slide bekommt `interactive360=true`. Der aus-
 *   fadende Slide wird kurzzeitig als **statischer Preview** (Poster/img) über
 *   `SmartMedia interactive360={false}` gerendert → max. 1 WebGL-Context.
 *
 * Konsumiert nur bereits vorhandene Bausteine (`SmartMedia`, `MediaRef`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SmartMedia from "@/components/SmartMedia";
import type { MediaRef } from "@/lib/media";
import { isEnabled } from "@/lib/siteContent";

export type HeroItem = {
    id?: string;
    /** Media-Referenz {path, media_id}. Auf media_id wird intern gefetched. */
    media?: MediaRef;
    /** Optionaler Untertitel (aktuell nicht standardmäßig gerendert). */
    label?: string;
    /** Sichtbarkeit. `false` blendet aus. Default true. */
    enabled?: boolean;
};

type Props = {
    items: HeroItem[];
    /** Statisches Fallback-Bild, wenn kein Item / media leer. */
    fallbackUrl?: string;
    fallbackAlt?: string;
    /** Auto-Advance-Intervall (ms). Default 6000. */
    autoAdvanceMs?: number;
    className?: string;
    /** DOM-Kinder oberhalb der Slides (z.B. Overlay-Gradient, Content). */
    children?: React.ReactNode;
    /** Wird bei jedem Slide-Wechsel aufgerufen — non-breaking Optional. */
    onSlideChange?: (idx: number, item: HeroItem) => void;
    testIdPrefix?: string;
};

const TRANSITION_MS = 800;

export default function HeroCarousel({
    items,
    fallbackUrl,
    fallbackAlt,
    autoAdvanceMs = 6000,
    className = "",
    children,
    onSlideChange,
    testIdPrefix = "hero-carousel",
}: Props) {
    const visibleItems = useMemo(() => (items || []).filter(isEnabled), [items]);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [previousIdx, setPreviousIdx] = useState<number | null>(null);
    const [reducedMotion, setReducedMotion] = useState(false);
    const pausedRef = useRef(false);
    const touchStartX = useRef<number | null>(null);
    const transitionTimeout = useRef<number | null>(null);

    // prefers-reduced-motion beobachten
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReducedMotion(mq.matches);
        const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    // Bei Wechsel der Item-Anzahl Index normalisieren
    useEffect(() => {
        if (currentIdx >= visibleItems.length) setCurrentIdx(0);
    }, [visibleItems.length, currentIdx]);

    // Callback bei Slide-Wechsel — für Parents, die z.B. den aktuellen
    // Label extern rendern möchten. Non-breaking Optional.
    useEffect(() => {
        if (onSlideChange && visibleItems[currentIdx]) {
            onSlideChange(currentIdx, visibleItems[currentIdx]);
        }
    }, [currentIdx, visibleItems, onSlideChange]);

    const goTo = useCallback((next: number) => {
        setCurrentIdx((cur) => {
            if (cur === next) return cur;
            setPreviousIdx(cur);
            if (transitionTimeout.current) window.clearTimeout(transitionTimeout.current);
            transitionTimeout.current = window.setTimeout(
                () => setPreviousIdx(null),
                reducedMotion ? 0 : TRANSITION_MS,
            );
            return next;
        });
    }, [reducedMotion]);

    const goNext = useCallback(() => {
        if (visibleItems.length < 2) return;
        setCurrentIdx((cur) => {
            const next = (cur + 1) % visibleItems.length;
            setPreviousIdx(cur);
            if (transitionTimeout.current) window.clearTimeout(transitionTimeout.current);
            transitionTimeout.current = window.setTimeout(
                () => setPreviousIdx(null),
                reducedMotion ? 0 : TRANSITION_MS,
            );
            return next;
        });
    }, [visibleItems.length, reducedMotion]);

    // Auto-Advance
    useEffect(() => {
        if (visibleItems.length < 2 || reducedMotion) return;
        const iv = window.setInterval(() => {
            if (!pausedRef.current) goNext();
        }, autoAdvanceMs);
        return () => window.clearInterval(iv);
    }, [visibleItems.length, autoAdvanceMs, goNext, reducedMotion]);

    // Cleanup pending timeout on unmount
    useEffect(() => () => {
        if (transitionTimeout.current) window.clearTimeout(transitionTimeout.current);
    }, []);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current == null) return;
        const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
        const delta = endX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < 40) return;
        if (visibleItems.length < 2) return;
        if (delta < 0) {
            goNext();
        } else {
            const prev = (currentIdx - 1 + visibleItems.length) % visibleItems.length;
            goTo(prev);
        }
    };

    // Empty state → Fallback-Bild
    if (visibleItems.length === 0) {
        return (
            <div className={className || "relative"} data-testid={`${testIdPrefix}-empty`}>
                {fallbackUrl && (
                    <img
                        src={fallbackUrl}
                        alt={fallbackAlt || ""}
                        loading="eager"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                )}
                {children}
            </div>
        );
    }

    const current = visibleItems[currentIdx];
    const previous = previousIdx != null ? visibleItems[previousIdx] : null;
    const isMulti = visibleItems.length >= 2;

    return (
        <div
            className={className || "relative"}
            data-testid={testIdPrefix}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
            onFocusCapture={() => { pausedRef.current = true; }}
            onBlurCapture={() => { pausedRef.current = false; }}
            onTouchStart={isMulti ? onTouchStart : undefined}
            onTouchEnd={isMulti ? onTouchEnd : undefined}
        >
            {/* Slide-Layer: alter Slide (fading out, non-interactive) */}
            {previous && (
                <SlideLayer
                    key={`prev-${previousIdx}`}
                    item={previous}
                    interactive={false}
                    isCurrent={false}
                    reducedMotion={reducedMotion}
                    fallbackUrl={fallbackUrl}
                    fallbackAlt={fallbackAlt}
                    testId={`${testIdPrefix}-slide-prev`}
                />
            )}
            {/* Slide-Layer: aktueller Slide (interactive für 360°) */}
            <SlideLayer
                key={`cur-${currentIdx}`}
                item={current}
                interactive
                isCurrent
                reducedMotion={reducedMotion}
                fallbackUrl={fallbackUrl}
                fallbackAlt={fallbackAlt}
                testId={`${testIdPrefix}-slide-current`}
            />

            {/* Content-Overlay (z.B. Gradient + Headlines) */}
            {children}

            {/* Dots-Navigation */}
            {isMulti && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 z-30 flex items-center gap-2"
                    style={{ bottom: "1.5rem" }}
                    data-testid={`${testIdPrefix}-dots`}
                >
                    {visibleItems.map((it, i) => {
                        const active = i === currentIdx;
                        return (
                            <button
                                key={it.id || i}
                                type="button"
                                aria-label={`Slide ${i + 1} von ${visibleItems.length}`}
                                aria-current={active ? "true" : undefined}
                                onClick={() => goTo(i)}
                                className="rounded-full transition-all"
                                style={{
                                    width: 8,
                                    height: 8,
                                    background: active ? "#C6A962" : "rgba(255,253,249,0.4)",
                                    boxShadow: active
                                        ? "0 0 0 3px rgba(198,169,98,0.18)"
                                        : "none",
                                    border: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                }}
                                data-testid={`${testIdPrefix}-dot-${i}`}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* ---------- Slide-Layer ---------- */
function SlideLayer({
    item, interactive, isCurrent, reducedMotion, fallbackUrl, fallbackAlt, testId,
}: {
    item: HeroItem;
    interactive: boolean;
    isCurrent: boolean;
    reducedMotion: boolean;
    fallbackUrl?: string;
    fallbackAlt?: string;
    testId?: string;
}) {
    return (
        <div
            className="absolute inset-0"
            style={{
                opacity: isCurrent ? 1 : 0,
                transition: reducedMotion
                    ? "none"
                    : `opacity ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            }}
            data-testid={testId}
            aria-hidden={!isCurrent}
        >
            <SmartMedia
                mediaRef={item.media}
                fallbackUrl={fallbackUrl}
                alt={fallbackAlt}
                className="w-full h-full object-cover"
                interactive360={interactive}
                videoAutoplay
                videoMuted
                videoLoop
                videoControls={false}
                showTypeBadge={false}
                loading="eager"
            />
        </div>
    );
}

/**
 * Legacy-Adapter: nimmt ein evtl. altes Section-JSON mit `background`-Media
 * und optional neues `items`-Array — liefert ein normalisiertes HeroItem[].
 * Wird von Home/Sylt verwendet, damit die Seite ohne Migration weiterläuft.
 */
export function heroItemsWithFallback(
    items: unknown,
    legacyBackground: MediaRef,
): HeroItem[] {
    if (Array.isArray(items) && items.length > 0) {
        return items as HeroItem[];
    }
    if (legacyBackground) {
        return [{ id: "legacy", media: legacyBackground, enabled: true }];
    }
    return [];
}
